import { Badge } from "@/components/ui/badge";
import {
  Network,
  Server,
  AppWindow,
  ShieldAlert,
  Briefcase,
} from "lucide-react";

const ICONS = {
  network: Network,
  infrastructure: Server,
  application: AppWindow,
  security: ShieldAlert,
  business: Briefcase,
};

const DOMAIN_ACCENT = {
  network: "text-sky-400 border-sky-500/30 bg-sky-500/5",
  infrastructure: "text-violet-300 border-violet-500/30 bg-violet-500/5",
  application: "text-emerald-300 border-emerald-500/30 bg-emerald-500/5",
  security: "text-rose-300 border-rose-500/30 bg-rose-500/5",
  business: "text-amber-300 border-amber-500/30 bg-amber-500/5",
};

function confidenceColor(c) {
  if (c >= 0.7) return "text-emerald-400";
  if (c >= 0.5) return "text-amber-300";
  return "text-rose-400";
}

export default function AgentCard({ hyp, index = 0 }) {
  const Icon = ICONS[hyp.domain] || AppWindow;
  const accent = DOMAIN_ACCENT[hyp.domain] || "";
  const conf = Number(hyp.confidence || 0);

  return (
    <div
      data-testid={`agent-card-${hyp.domain}`}
      className="cre-surface rounded-sm p-5 cre-fade-up"
      style={{ animationDelay: `${index * 80}ms` }}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-3">
          <div
            className={`h-9 w-9 flex items-center justify-center rounded-sm border ${accent}`}
          >
            <Icon className="h-4 w-4" strokeWidth={1.5} />
          </div>
          <div>
            <div className="font-heading text-sm font-semibold tracking-tight">
              {hyp.agent_name}
            </div>
            <div className="cre-overline">{hyp.domain} domain</div>
          </div>
        </div>
        <div className="text-right">
          <div className="cre-overline">Confidence</div>
          <div className={`font-mono text-lg font-semibold ${confidenceColor(conf)}`}>
            {(conf * 100).toFixed(0)}%
          </div>
        </div>
      </div>

      <div className="mt-4 text-sm text-zinc-200 font-medium">{hyp.hypothesis}</div>
      <p className="mt-2 text-sm text-zinc-400 leading-relaxed">{hyp.reasoning}</p>

      {hyp.evidence?.length ? (
        <div className="mt-3">
          <div className="cre-overline mb-1.5">Evidence</div>
          <ul className="space-y-1">
            {hyp.evidence.map((e, i) => (
              <li key={i} className="text-xs text-zinc-400 font-mono flex gap-2">
                <span className="text-zinc-600">›</span>
                <span>{e}</span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {hyp.missing_telemetry?.length ? (
        <div className="mt-3">
          <div className="cre-overline mb-1.5 text-amber-300">
            Missing telemetry
          </div>
          <ul className="space-y-1">
            {hyp.missing_telemetry.map((m, i) => (
              <li
                key={i}
                className="text-xs text-zinc-400 font-mono flex gap-2"
              >
                <span className="text-amber-400">▢</span>
                <span>{m}</span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {hyp.sub_hypotheses?.length ? (
        <div
          className="mt-4 border border-emerald-500/15 rounded-sm p-3 bg-emerald-500/[0.03]"
          data-testid={`agent-sub-hypotheses-${hyp.domain}`}
        >
          <div className="cre-overline text-emerald-300 mb-2">
            Application Service · internal sub-capabilities
          </div>
          <div className="space-y-2.5">
            {hyp.sub_hypotheses.map((s, i) => (
              <div
                key={i}
                data-testid={`sub-hyp-${s.capability}`}
                className="border border-white/5 rounded-sm p-2.5 bg-black/20"
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="font-mono text-[11px] uppercase tracking-widest text-emerald-300">
                    {String.fromCharCode(65 + i)} · {s.title}
                  </span>
                  <span
                    className={`font-mono text-[11px] ${confidenceColor(
                      Number(s.confidence || 0)
                    )}`}
                  >
                    {Math.round(Number(s.confidence || 0) * 100)}%
                  </span>
                </div>
                <div className="mt-1.5 text-xs text-zinc-200 leading-snug">
                  {s.hypothesis}
                </div>
                {s.evidence?.length ? (
                  <div className="mt-1.5 text-[11px] text-zinc-500 font-mono">
                    evidence: {s.evidence.join(" · ")}
                  </div>
                ) : null}
                {s.missing_telemetry?.length ? (
                  <div className="mt-0.5 text-[11px] text-amber-400/80 font-mono">
                    needs: {s.missing_telemetry.join(" · ")}
                  </div>
                ) : null}
              </div>
            ))}
          </div>
        </div>
      ) : null}

      {hyp.proposed_actions?.length ? (
        <div className="mt-4">
          <div className="cre-overline mb-1.5">Proposed actions</div>
          <div className="space-y-2">
            {hyp.proposed_actions.map((a, i) => (
              <div
                key={i}
                className="border border-white/10 rounded-sm p-2.5 text-xs"
                data-testid={`agent-action-${hyp.domain}-${i}`}
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="font-medium text-zinc-100">{a.action}</span>
                  <Badge
                    variant="outline"
                    className={`text-[10px] font-mono uppercase rounded-sm ${
                      a.risk === "high"
                        ? "border-rose-500/40 text-rose-300"
                        : a.risk === "medium"
                          ? "border-amber-500/40 text-amber-300"
                          : "border-emerald-500/40 text-emerald-300"
                    }`}
                  >
                    {a.risk} risk
                  </Badge>
                </div>
                {a.rollback ? (
                  <div className="mt-1 text-zinc-500">
                    <span className="cre-overline mr-1">Rollback:</span>
                    <span className="font-mono">{a.rollback}</span>
                  </div>
                ) : null}
              </div>
            ))}
          </div>
        </div>
      ) : null}

      <div className="mt-4 pt-3 border-t border-white/5 flex items-center justify-between">
        <div className="flex gap-3 text-[10px] font-mono text-zinc-500">
          {Object.entries(hyp.domain_scores || {}).map(([k, v]) => (
            <span key={k}>
              <span className="text-zinc-600 mr-0.5">{k}</span>
              {Number(v).toFixed(2)}
            </span>
          ))}
        </div>
        <span
          className={`text-[10px] font-mono uppercase tracking-widest ${
            hyp.source === "llm" ? "text-emerald-400" : "text-amber-400"
          }`}
        >
          {hyp.source === "llm" ? "· LLM" : "· fallback"}
        </span>
      </div>
    </div>
  );
}
